// All localStorage / sessionStorage reads and writes for the In-App Review Flow.
// Frontend-only — backend has no review_state table.
//
// Persistent flags live in localStorage and survive across browser sessions.
// The session lock lives in sessionStorage so a single browser session never sees
// more than one review prompt regardless of how many triggers fire.

const KEYS = Object.freeze({
  ALREADY_REVIEWED: "review_already_reviewed",
  NORMALIZATION_SHOWN: "review_normalization_shown",
  LAST_PROMPT_SHOWN_AT: "review_last_prompt_shown_at",
  LAST_PROMPT_TRIGGER: "review_last_prompt_trigger",
  PROMPT_COUNT_MONTH: "review_prompt_count_this_month",
  PROMPT_COUNT_RESET: "review_prompt_count_reset_date",
  SHOWN_THIS_SESSION: "review_prompt_shown_this_session",
});

const safeGet = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeSet = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch (e) {
    // Storage may be disabled (incognito quota, privacy settings). Silently no-op.
  }
};

// ---------- Reads ----------

export const isAlreadyReviewed = () =>
  safeGet(window.localStorage, KEYS.ALREADY_REVIEWED) === "true";

export const isNormalizationShown = () =>
  safeGet(window.localStorage, KEYS.NORMALIZATION_SHOWN) === "true";

export const getLastPromptShownAt = () => {
  const iso = safeGet(window.localStorage, KEYS.LAST_PROMPT_SHOWN_AT);
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

export const getLastPromptTrigger = () =>
  safeGet(window.localStorage, KEYS.LAST_PROMPT_TRIGGER);

// Monthly auto-reset happens here on every read so callers never need to think about it.
// If the stored reset date is in a prior calendar month (or missing while a count exists),
// the count is zeroed and the reset date is moved to the first day of the current month.
export const getPromptCountThisMonth = () => {
  const countRaw = safeGet(window.localStorage, KEYS.PROMPT_COUNT_MONTH);
  const resetRaw = safeGet(window.localStorage, KEYS.PROMPT_COUNT_RESET);

  const now = new Date();
  const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  if (resetRaw) {
    const resetDate = new Date(resetRaw);
    const sameMonth =
      !isNaN(resetDate.getTime()) &&
      resetDate.getFullYear() === now.getFullYear() &&
      resetDate.getMonth() === now.getMonth();

    if (!sameMonth) {
      safeSet(window.localStorage, KEYS.PROMPT_COUNT_MONTH, "0");
      safeSet(window.localStorage, KEYS.PROMPT_COUNT_RESET, firstOfThisMonth.toISOString());
      return 0;
    }
  }

  const parsed = parseInt(countRaw, 10);
  return isNaN(parsed) ? 0 : parsed;
};

export const isShownThisSession = () =>
  safeGet(window.sessionStorage, KEYS.SHOWN_THIS_SESSION) === "true";

// ---------- Writes ----------

export const markAlreadyReviewed = () => {
  safeSet(window.localStorage, KEYS.ALREADY_REVIEWED, "true");
};

export const clearAlreadyReviewed = () => {
  try {
    window.localStorage.removeItem(KEYS.ALREADY_REVIEWED);
  } catch (e) {
    // ignore
  }
};

export const markNormalizationShown = () => {
  safeSet(window.localStorage, KEYS.NORMALIZATION_SHOWN, "true");
};

// Records that a review prompt was actually shown to the user for `trigger`.
// Called from useReviewFlow.triggerReview() and useReviewFlow.acknowledgeInlineShown().
// Updates: last shown timestamp, last trigger, monthly count, monthly reset date,
// and the session lock.
export const recordPromptShown = (trigger) => {
  const now = new Date();
  const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  safeSet(window.localStorage, KEYS.LAST_PROMPT_SHOWN_AT, now.toISOString());
  safeSet(window.localStorage, KEYS.LAST_PROMPT_TRIGGER, trigger);

  // Read via getPromptCountThisMonth so the monthly auto-reset rule is applied before increment.
  const current = getPromptCountThisMonth();
  safeSet(window.localStorage, KEYS.PROMPT_COUNT_MONTH, String(current + 1));

  if (!safeGet(window.localStorage, KEYS.PROMPT_COUNT_RESET)) {
    safeSet(window.localStorage, KEYS.PROMPT_COUNT_RESET, firstOfThisMonth.toISOString());
  }

  safeSet(window.sessionStorage, KEYS.SHOWN_THIS_SESSION, "true");
};

// Dev-only utility for QA / manual testing. Not wired into any production flow.
export const resetReviewStorage = () => {
  try {
    Object.values(KEYS).forEach((key) => {
      window.localStorage.removeItem(key);
      window.sessionStorage.removeItem(key);
    });
  } catch (e) {
    // ignore
  }
};
